import { Link, useLocation } from "react-router";
import { getActiveSectionPath } from "@app/components/Breadcrumb/helpers";
import { RouteFactory } from "@app/routePaths";
import Ad from "@components/Advertisement/Ad";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import { GADataProps } from "@components/GAData/GAData";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import Header from "@components/Header/Header";
import { useBreadCrumbState } from "@components/Header/helpers";
import ListingPageNewsletter from "@features/newsletter/ListingPageNewsletter";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { getBreadcrumbProperties } from "@features/seo/helpers/getJsonLdProperties";
import { useInjectClientData } from "@hooks/useInjectClientData";
import { useWindowSize } from "@hooks/useWindowSize";
import Listing from "@particles/listing/Listing";
import ThriveNewsletter from "@routes/Article/components/ArticleNewsletter/ThriveNewsletter";
import { getUrlPathFromAbsoluteUrl } from "@routes/Article/utils/helpers";
import BreakingNews from "@routes/Section/components/BreakingNews/BreakingNews";
import MostPopular from "@routes/Section/components/MostPopular/MostPopular";
import PrPageIntroduction from "@routes/Section/components/PrPageIntroduction";
import SectionHeading from "@routes/Section/components/SectionHeading";
import SectionSubhHeading from "@routes/Section/components/SectionSubhHeading";
import {
  getListingUniqueName,
  getSectionHeadingClass,
  getSectionHeadingText,
} from "@routes/Section/utils/helpers";
import { DefaultSectionPageData } from "@routes/Section/utils/types";
import { USFBreadcrumbSchema } from "@sphtech/web2-seo-schema";
import { sectionNavigationItems } from "@util/constant";
import { cn } from "@util/helpers";

const DefaultLayout = ({
  entity,
  articles,
  seoFooterData,
  gaProps,
}: DefaultSectionPageData & { gaProps: GADataProps }): React.ReactElement => {
  const { pathname } = useLocation();
  const { isScreenLG } = useWindowSize();

  const { urlPath, parent, uniqueName } = entity;

  const relativePath = getUrlPathFromAbsoluteUrl(urlPath);
  const navKey = relativePath.replace("/", "");
  const parentRelativePath = getUrlPathFromAbsoluteUrl(parent?.urlPath || "");

  const parentNavKey = parentRelativePath.replace("/", "");
  const childNavKey = relativePath.replace("/", "");
  const parentNavData = parentNavKey
    ? sectionNavigationItems?.[parentNavKey]
    : sectionNavigationItems?.[childNavKey];

  const navData = sectionNavigationItems?.[childNavKey];

  const activeSectionPath = getActiveSectionPath(pathname, relativePath);
  const { breadcrumbsRef, isShowBreadCrumb } = useBreadCrumbState();

  const sectionHeadingClass = getSectionHeadingClass(relativePath);
  const sectionHeadingLabel = navData?.label || "";
  const sectionHeadingText = getSectionHeadingText(
    relativePath,
    sectionHeadingLabel
  );

  const _uniqueName = getListingUniqueName(uniqueName);
  const breadcrumbList = getBreadcrumbProperties(entity);

  useInjectClientData({ gaProps });

  return (
    <div className="w-full" data-testid="default-section-layout-component">
      <USFBreadcrumbSchema items={breadcrumbList} />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header
        displayBreadcrumb={isShowBreadCrumb}
        parentCategory={parentNavKey || childNavKey}
        childCategory={childNavKey}
        categoryDataMapping={sectionNavigationItems}
      />

      {pathname !== RouteFactory.paidPressRelease ? <PulseEntry /> : null}

      {relativePath && sectionNavigationItems[navKey] ? (
        <div className="flex-none">
          <Container>
            <SectionHeading
              text={sectionHeadingText}
              className={sectionHeadingClass}
              hasBorder={!parentNavData?.children?.length}
            />

            <>
              {!isScreenLG && pathname.startsWith(RouteFactory.thrive) ? (
                <ThriveNewsletter
                  className="pb-3"
                  tagLine="Straight to your inbox."
                  liner="Money, career and life hacks to help young adults stay ahead."
                />
              ) : null}

              {relativePath.startsWith(RouteFactory.newsletters) ? (
                <SectionSubhHeading
                  to={`${RouteFactory.newsletterSignup}?ref=listing-menubar`}
                  className="inline-block pb-3 font-poppins text-xs font-light text-gray-850 underline"
                >
                  Select Your Newsletters
                </SectionSubhHeading>
              ) : null}
            </>

            <div ref={breadcrumbsRef}>
              <Breadcrumb
                className="m-auto"
                parentCategory={parentNavKey || childNavKey}
                childCategory={childNavKey}
                categoryDataMapping={sectionNavigationItems}
                extraBreadcrumbItems={
                  <div
                    className={cn(
                      "relative box-border flex shrink-0 items-center border-r border-gray-175 pr-3 font-poppins text-sm tracking-tightest text-gray-850 hover:opacity-80",
                      {
                        "font-bold": activeSectionPath === parentNavData?.link,
                      }
                    )}
                  >
                    <Link
                      to={`${parentNavData?.link}?ref=listing-menubar`}
                      reloadDocument
                    >
                      Top stories
                    </Link>
                  </div>
                }
              />
            </div>
          </Container>
        </div>
      ) : null}

      <Container rootClassName="pt-6">
        <>
          {pathname === RouteFactory.paidPressRelease ? (
            <PrPageIntroduction />
          ) : null}
        </>

        <Row>
          <Column rootClassName="w-full lg:w-8/12 mb-6">
            <Listing
              cardProps={articles}
              pageType={"section"}
              size={10}
              isUseTemplateH
              sections={_uniqueName}
            />
          </Column>

          <Column rootClassName="w-full lg:w-4/12 mb-6">
            {isScreenLG && pathname.startsWith(RouteFactory.thrive) ? (
              <ThriveNewsletter
                className="mx-auto mb-6 max-w-[300px]"
                tagLine="Straight to your inbox."
                liner="Money, career and life hacks to help young adults stay ahead."
              />
            ) : null}

            {isScreenLG ? (
              <ListingPageNewsletter className="mx-auto mb-6 max-w-[300px]" />
            ) : null}

            <div className="mb-6 lg:h-[800px]">
              <Ad
                adType="imu2"
                className="flex flex-col items-center justify-center lg:!sticky lg:top-18"
              />
            </div>

            <BreakingNews rootClassName="max-w-[300px] mx-auto mb-6" />

            <MostPopular rootClassName="max-w-[300px] mx-auto" />
          </Column>
        </Row>
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />
      <Ad adType="prestitial" />
    </div>
  );
};

export default DefaultLayout;
